import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";

import GalleryMediaModalItem from "../Media/GalleryMediaModalItem";
import Portal from "./Portal";
import GalleryThumbnail from "../Media/GalleryThumbnail";

import { galleryActions } from "../../context/gallery";

const GalleryModal = forwardRef((props, ref) => {
  const { galleryItems, modalClassName, modalDialogClassName } = props;

  const dispatch = useDispatch();
  const storeCurrentIndex = useSelector((state) => state.gallery.currentIndex);

  const [display, setDisplay] = useState(false);

  const mainItemRef = useRef();
  const modalElRef = useRef();
  const thumbnailsRef = useRef();

  useEffect(() => {
    thumbnailsRef?.current?.slickGoTo(storeCurrentIndex);
    mainItemRef?.current?.slickGoTo(storeCurrentIndex);
  }, [storeCurrentIndex]);

  // Handle closing the modal with escape key
  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        handleClose();
      }
    };
    window.addEventListener("keydown", close);

    return () => window.removeEventListener("keydown", close);
  }, []);

  // Expose open and close functions
  useImperativeHandle(ref, () => {
    return {
      openModal: () => handleOpen(),
      closeModal: () => handleClose(),
    };
  });

  const handleOpen = () => {
    setDisplay(true);
    modalElRef?.current?.focus(); // To ensure arrow keyboard presses work
  };

  const handleClose = () => {
    setDisplay(false);
  };

  // Allow keyboard left and right to scroll items
  const onKeyDownHandler = (ev) => {
    // Left
    if (ev?.keyCode === 37 && storeCurrentIndex > 0) {
      dispatch(galleryActions.prevIndex());
    }

    // Right
    if (ev?.keyCode === 39 && storeCurrentIndex < props.totalItems - 1) {
      dispatch(galleryActions.nextIndex());
    }
  };

  const onSwipeHandler = (swipeDirection) => {
    swipeDirection === "left"
      ? dispatch(galleryActions.nextIndex())
      : dispatch(galleryActions.prevIndex());
  };

  // Handle selecting a thumbnail to update main image
  const updateMainImage = (selectedIndex) => {
    dispatch(galleryActions.setCurrentIndex(selectedIndex));
  };

  const galleryThumbnailsMap = galleryItems?.map((item, index) => {
    const isVideoThumbnail = item.youtube || item.video.vimeo_raw;

    return (
      <GalleryThumbnail
        key={index}
        backgroundImageFilename={item?.image?.filename}
        className={`${
          index === storeCurrentIndex ? "gallery-thumbnail-item-active" : ""
        }`}
        isVideoThumbnail={isVideoThumbnail}
        onClickHandler={() => updateMainImage(index)}
      />
    );
  });

  const galleryModalItemsMap = galleryItems?.map((item, index) => {
    return <GalleryMediaModalItem key={index} data={item} itemIndex={index} />;
  });

  if (display) {
    return (
      <Portal>
        <div
          className={`brand-gallery-modal ${modalClassName}`}
          tabIndex="-1"
          role="none"
          onKeyDown={onKeyDownHandler}
          ref={modalElRef}
        >
          <div
            className="brand-modal-background"
            onClick={handleClose}
            onKeyDown={handleClose}
            role="none"
            tabIndex="-1"
          />

          <div
            className={`brand-gallery-modal-content ${modalDialogClassName}`}
            role="dialog"
          >
            <FontAwesomeIcon
              style={{ height: "32px", width: "32px" }}
              className="ms-auto brand-text-white brand-cursor brand-gallery-modal-close-button"
              icon="times"
              onClick={handleClose}
            />

            <div className="brand-gallery-modal-content-main-item">
              <div className="brand-gallery-modal-content-main-item-wrapper d-flex justify-content-center align-items-center">
                <Slider
                  style={{ height: "100%", width: "100%" }}
                  ref={mainItemRef}
                  infinite={false}
                  asNavFor={thumbnailsRef.current}
                  initialSlide={storeCurrentIndex}
                  adaptiveHeight={true}
                  onSwipe={onSwipeHandler}
                >
                  {galleryModalItemsMap}
                </Slider>
              </div>

              <svg
                className="gallery-button gallery-button-left"
                viewBox="0 0 30 80"
                onClick={() => dispatch(galleryActions.prevIndex())}
              >
                <FontAwesomeIcon icon="chevron-left" />
              </svg>

              <svg
                className="gallery-button gallery-button-right"
                viewBox="0 0 30 80"
                onClick={() => dispatch(galleryActions.nextIndex())}
              >
                <FontAwesomeIcon icon="chevron-right" />
              </svg>
            </div>
            <div className="brand-gallery-modal-content-thumbnails-container">
              <Slider
                style={{ height: "100%", width: "100%" }}
                onSwipe={onSwipeHandler}
                asNavFor={mainItemRef.current}
                infinite={false}
                ref={thumbnailsRef}
                slidesToShow={8}
                slidesToScroll={1}
                initialSlide={storeCurrentIndex}
                focusOnSelect={true}
                responsive={[
                  { breakpoint: 1024, settings: { slidesToShow: 6 } },
                  { breakpoint: 768, settings: { slidesToShow: 3 } },
                  {
                    breakpoint: 1,
                    settings: { slidesToShow: 2 },
                  },
                ]}
              >
                {galleryThumbnailsMap}
              </Slider>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  return null;
});

export default GalleryModal;
GalleryModal.propTypes = {
  galleryItems: PropTypes.array,
  modalClassName: PropTypes.string,
  modalDialogClassName: PropTypes.string,
};

GalleryModal.defaultProps = {
  galleryItems: [],
  modalClassName: "",
  modalDialogClassName: "",
};
